import { useState } from 'react';

import { useIdeaCatalogue, useIdeaPoints, useIdeas } from '@/api/queries';
import type { Dimension, IdeaCatalogue } from '@/api/types';

import { useIdeaFilters } from './ideaFilters';

const NO_COUNTS = { active: 0, parked: 0, archived: 0 };
const NO_OUTCOME_COUNTS = { go: 0, conditional_go: 0, no_go: 0, researching: 0, failed: 0 };
const NO_DIMENSIONS: Dimension[] = [];

/** The industries offered by the industry filter: Unassigned, then approved, then those pending review. */
export function industryOptions(industries: IdeaCatalogue['industries']) {
  return [
    { value: 'unassigned', label: 'Unassigned' },
    ...industries
      .filter((industry) => industry.status === 'approved')
      .map((industry) => ({ value: industry.id, label: industry.is_active ? industry.name : `${industry.name} (inactive)` })),
    ...industries
      .filter((industry) => industry.status === 'pending')
      .map((industry) => ({ value: industry.id, label: `${industry.name} (pending review)` })),
  ];
}

/** The Business Opportunities page's filters, queries and toggles. */
export function useBusinessOpportunities() {
  const catalogue = useIdeaCatalogue();
  const filters = useIdeaFilters();
  const { view, params } = filters;
  const [showDimensions, setShowDimensions] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // One query per view: the list a page at a time, the matrix every match at once.
  const list = useIdeas(params, view === 'list');
  const matrix = useIdeaPoints(params, view === 'matrix');
  const ideas = view === 'list' ? list : matrix;

  return {
    catalogue,
    filters,
    list,
    matrix,
    ideas,
    dimensions: catalogue.data?.dimensions ?? NO_DIMENSIONS,
    counts: ideas.data?.counts ?? NO_COUNTS,
    outcomeCounts: ideas.data?.verdict_counts ?? NO_OUTCOME_COUNTS,
    showDimensions,
    setShowDimensions,
    showGuide,
    setShowGuide,
  };
}
